"use client";

import React from "react";
import { EnhancedErrorDisplay, SuccessDisplay } from "./enhanced-error-display";
import { useFeedback } from "@/lib/hooks/forms/use-feedback";

interface FeedbackDisplayProps {
  feedback: ReturnType<typeof useFeedback>;
  errorTitle?: string;
  showErrorDetails?: boolean;
  className?: string;
  variant?: "form" | "table" | "general";
  position?: "top" | "bottom" | "inline";
  autoDismiss?: boolean;
  dismissDelay?: number;
}

export const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({
  feedback,
  errorTitle = "Operation Failed",
  showErrorDetails = true,
  className = "mt-4",
  variant = "general",
  position = "inline",
  autoDismiss = false,
  dismissDelay = 5000,
}) => {
  // Auto-dismiss functionality
  React.useEffect(() => {
    if (autoDismiss && (feedback.hasSuccess || feedback.hasError)) {
      const timer = setTimeout(() => {
        if (feedback.hasSuccess) {
          feedback.dismissSuccess();
        } else if (feedback.hasError) {
          feedback.dismissError();
        }
      }, dismissDelay);

      return () => clearTimeout(timer);
    }
  }, [feedback.hasSuccess, feedback.hasError, autoDismiss, dismissDelay, feedback]);

  // Determine styling based on variant and position
  const getContainerClasses = () => {
    const baseClasses = "transition-all duration-300 ease-in-out";

    if (variant === "table") {
      return `${baseClasses} sticky z-10 ${position === "top" ? "top-0" : "bottom-0"} bg-card border-b border-gray-200 dark:border-gray-700 shadow-sm`;
    }

    if (variant === "form") {
      return `${baseClasses} ${position === "inline" ? "mt-4" : "mb-4"}`;
    }

    return `${baseClasses} ${className}`;
  };

  // Determine success styling
  const getSuccessClasses = () => {
    if (variant === "table") {
      return "rounded-none border-l-4 border-l-green-500 bg-green-50 dark:bg-green-950/40";
    }
    return "rounded-md border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40";
  };

  // Determine error styling
  const getErrorClasses = () => {
    if (variant === "table") {
      return "rounded-none border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/40";
    }
    return "rounded-md border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40";
  };

  return (
    <div className={getContainerClasses()}>
      {/* Success Message */}
      {feedback.hasSuccess && (
        <SuccessDisplay
          message={feedback.successMessage}
          onDismiss={feedback.dismissSuccess}
          className={`mb-4 ${getSuccessClasses()}`}
        />
      )}

      {/* Error Message */}
      {feedback.hasError && (
        <EnhancedErrorDisplay
          error={feedback.error}
          title={errorTitle}
          showDetails={showErrorDetails}
          onDismiss={feedback.dismissError}
          className={`mb-4 ${getErrorClasses()}`}
        />
      )}
    </div>
  );
};

// Backward compatibility aliases
export const FormFeedbackDisplay = FeedbackDisplay;
export const TableFeedbackDisplay = FeedbackDisplay;
